/**
 * MusicScheduler: vanilla-style sparse music. Waits a random gap, plays one
 * track, waits again. Real tracks from the manifest (`music.<mood>`) stream
 * through an <audio> element; otherwise a procedural C418-flavoured piece is
 * generated live: slow modal/pentatonic piano arpeggios and melody over soft
 * pads, through a long procedurally-synthesized convolution reverb.
 */
import type { AudioManager } from './AudioManager';
import { AUDIO_BASE } from './AudioManager';
import { musicFiles, pickVariant } from './manifest';
import type { MusicMood } from './registry';
import { FIRST_DELAY, SCALES, degreeToMidi, gapFor, midiHz, pieceLength, waitAfterMoodChange } from './musicMath';
import { makeImpulse } from './synth';

const FADE = 2;

interface Track { stop(fade: number): void; readonly done: boolean; update(): void }

/** Live generative piece. */
class ProceduralPiece implements Track {
  done = false;
  private readonly out: GainNode;
  private readonly end: number;
  private next: number;
  private nextBass: number;
  private nextPad: number;
  private readonly root: number;
  private readonly scale: number[];
  private deg = 0;
  private phraseLeft = 0;
  private motif: number[] = [];
  private motifPos = 0;
  private readonly beat: number;
  private stopped = false;

  constructor(private readonly ctx: AudioContext, dest: AudioNode, reverb: AudioNode,
    private readonly mood: MusicMood) {
    const rnd = Math.random;
    this.out = ctx.createGain();
    this.out.gain.value = 0;
    this.out.gain.linearRampToValueAtTime(mood === 'nether' ? 0.5 : 0.42, ctx.currentTime + 3);
    this.out.connect(dest);
    this.out.connect(reverb);
    const scales = SCALES[mood];
    this.scale = scales[Math.floor(rnd() * scales.length)];
    this.root = (mood === 'nether' ? 45 : 57) + Math.floor(rnd() * 7) - 3;
    this.beat = mood === 'nether' ? 0.9 + rnd() * 0.5 : 0.55 + rnd() * 0.45;
    const t = ctx.currentTime + 0.5;
    this.next = t + 1.5;
    this.nextBass = t;
    this.nextPad = t + (mood === 'menu' ? 0 : 4);
    this.end = t + pieceLength(mood, rnd);
    this.newMotif();
  }

  private newMotif(): void {
    const n = 4 + Math.floor(Math.random() * 3);
    this.motif = [];
    let d = Math.floor(Math.random() * 3);
    for (let i = 0; i < n; i++) { this.motif.push(d); d += [-2, -1, 1, 2, 3][Math.floor(Math.random() * 5)]; }
    this.motifPos = 0;
  }

  /** Piano-ish tone: a few decaying partials with a soft hammer. */
  private piano(t: number, midi: number, vel: number, dest: AudioNode = this.out): void {
    const ctx = this.ctx;
    const f = midiHz(midi);
    const len = 3.5 + Math.random() * 2.5;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(vel, t + 0.008);
    g.gain.exponentialRampToValueAtTime(vel * 0.35, t + 0.4);
    g.gain.exponentialRampToValueAtTime(0.0001, t + len);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(Math.min(6000, f * 6), t);
    lp.frequency.exponentialRampToValueAtTime(Math.max(200, f * 1.5), t + len);
    lp.connect(g).connect(dest);
    const partials: [number, number][] = [[1, 1], [2, 0.35], [3, 0.12], [4.01, 0.06]];
    for (const [m, a] of partials) {
      const o = ctx.createOscillator();
      o.type = 'sine';
      o.frequency.value = f * m;
      o.detune.value = (Math.random() - 0.5) * 6;
      const pg = ctx.createGain();
      pg.gain.value = a;
      o.connect(pg).connect(lp);
      o.start(t); o.stop(t + len + 0.05);
    }
  }

  private pad(t: number, midis: number[], len: number): void {
    const ctx = this.ctx;
    const g = ctx.createGain();
    const lvl = this.mood === 'nether' ? 0.05 : 0.035;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(lvl, t + len * 0.4);
    g.gain.linearRampToValueAtTime(0.0001, t + len);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = this.mood === 'nether' ? 500 : 900;
    lp.Q.value = 0.5;
    lp.connect(g).connect(this.out);
    for (const m of midis) {
      for (const det of [-7, 7]) {
        const o = ctx.createOscillator();
        o.type = this.mood === 'nether' ? 'sawtooth' : 'triangle';
        o.frequency.value = midiHz(m);
        o.detune.value = det;
        o.connect(lp);
        o.start(t); o.stop(t + len + 0.1);
      }
    }
  }

  update(): void {
    if (this.done) return;
    const now = this.ctx.currentTime;
    if (!this.stopped && now >= this.end) this.stop(6);
    if (this.stopped) return;
    const horizon = now + 1.0;
    // melody: phrases of a repeated / varied motif, separated by rests
    while (this.next < horizon) {
      const t = this.next;
      if (this.phraseLeft <= 0) {
        this.phraseLeft = 3 + Math.floor(Math.random() * 6);
        if (Math.random() < 0.35) this.newMotif();
        this.motifPos = 0;
        this.next = t + this.beat * (2 + Math.floor(Math.random() * 5));
        continue;
      }
      this.phraseLeft--;
      const vary = Math.random() < 0.25 ? (Math.random() < 0.5 ? -1 : 1) : 0;
      this.deg = this.motif[this.motifPos++ % this.motif.length] + vary;
      const octave = this.mood === 'nether' ? 0 : this.scale.length;
      const midi = degreeToMidi(this.root, this.scale, this.deg + octave);
      this.piano(t, midi, 0.16 + Math.random() * 0.08);
      // occasional dyad for colour
      if (Math.random() < 0.2) this.piano(t + 0.02, degreeToMidi(this.root, this.scale, this.deg + octave - 2), 0.08);
      const dur = [1, 1, 1, 2, 2, 0.5, 3][Math.floor(Math.random() * 7)];
      this.next = t + this.beat * dur * (0.95 + Math.random() * 0.1);
    }
    // left hand: a low root / fifth arpeggio every few beats
    while (this.nextBass < horizon) {
      const t = this.nextBass;
      const chordDeg = [0, 3, 4, 5, -2][Math.floor(Math.random() * 5)];
      const base = degreeToMidi(this.root - 12, this.scale, chordDeg);
      this.piano(t, base, 0.12);
      this.piano(t + this.beat, base + 7, 0.07);
      if (Math.random() < 0.5) this.piano(t + this.beat * 2, base + 12, 0.06);
      this.nextBass = t + this.beat * (6 + Math.floor(Math.random() * 6));
    }
    while (this.nextPad < horizon) {
      const t = this.nextPad;
      const len = 10 + Math.random() * 10;
      if (Math.random() < (this.mood === 'nether' ? 0.9 : 0.6)) {
        const d = Math.floor(Math.random() * this.scale.length);
        this.pad(t, [degreeToMidi(this.root - 12, this.scale, d), degreeToMidi(this.root - 12, this.scale, d + 2), degreeToMidi(this.root, this.scale, d + 4)], len);
      }
      this.nextPad = t + len * 0.8;
    }
  }

  stop(fade: number): void {
    if (this.stopped) return;
    this.stopped = true;
    const t = this.ctx.currentTime;
    this.out.gain.cancelScheduledValues(t);
    this.out.gain.setValueAtTime(this.out.gain.value, t);
    this.out.gain.linearRampToValueAtTime(0, t + fade);
    // let scheduled notes ring into the reverb tail, then drop the graph
    setTimeout(() => { this.done = true; this.out.disconnect(); }, (fade + 7) * 1000);
  }
}

/** A real file streamed through an <audio> element. */
class FileTrack implements Track {
  done = false;
  private readonly el: HTMLAudioElement;
  private readonly g: GainNode;
  constructor(ctx: AudioContext, dest: AudioNode, url: string, onError: () => void) {
    this.el = new Audio(url);
    this.el.crossOrigin = 'anonymous';
    const src = ctx.createMediaElementSource(this.el);
    this.g = ctx.createGain();
    src.connect(this.g).connect(dest);
    this.el.addEventListener('ended', () => { this.done = true; });
    this.el.addEventListener('error', () => { this.done = true; onError(); });
    void this.el.play().catch(() => { this.done = true; });
  }
  update(): void { /* streaming */ }
  stop(fade: number): void {
    const t = this.g.context.currentTime;
    this.g.gain.setValueAtTime(this.g.gain.value, t);
    this.g.gain.linearRampToValueAtTime(0, t + fade);
    setTimeout(() => { this.el.pause(); this.done = true; }, fade * 1000);
  }
}

export class MusicScheduler {
  private mood: MusicMood | null = null;
  private wait = 0;
  private track: Track | null = null;
  private trackMood: MusicMood | null = null;
  private readonly reverb: ConvolverNode;
  private readonly lastFile = new Map<string, number>();
  private readonly badFiles = new Set<string>();

  constructor(private readonly am: AudioManager) {
    const ctx = am.ctx;
    this.reverb = ctx.createConvolver();
    this.reverb.buffer = makeImpulse(ctx, 5, 2.6);
    const wet = ctx.createGain();
    wet.gain.value = 0.55;
    this.reverb.connect(wet).connect(am.gain('music'));
  }

  get playing(): boolean { return !!this.track && !this.track.done; }

  /** Called every frame with the wanted mood. */
  update(dt: number, mood: MusicMood): void {
    if (mood !== this.mood) {
      const prev = this.mood;
      this.mood = mood;
      const firstDelay = (): number => FIRST_DELAY[0] + Math.random() * (FIRST_DELAY[1] - FIRST_DELAY[0]);
      if (this.track && this.trackMood !== mood) { this.track.stop(FADE); this.track = null; this.wait = Infinity; }
      if (mood === 'menu') this.wait = gapFor('menu', Math.random);
      // entering a world (from the title or first load): a short first delay, like vanilla
      else if (prev === null || prev === 'menu') this.wait = firstDelay();
      else this.wait = waitAfterMoodChange(this.wait, mood, Math.random);
    }
    this.track?.update();
    if (this.track && this.track.done) {
      this.track = null;
      this.wait = gapFor(mood, Math.random);
    }
    if (this.track) return;
    // music volume 0 stops scheduling entirely; a suspended context waits for the unlock
    if (!this.am.running || this.am.gain('music').gain.value <= 0.001 || this.am.gain('master').gain.value <= 0.001) return;
    this.wait -= dt;
    if (this.wait > 0) return;
    this.startTrack(mood);
  }

  /** Skip the wait (debug / testing). */
  playNow(): void { this.wait = 0; }

  private startTrack(mood: MusicMood): void {
    const files = musicFiles(this.am.manifest, mood).filter((f) => !this.badFiles.has(f));
    const dest = this.am.gain('music');
    this.trackMood = mood;
    if (files.length) {
      const i = pickVariant(files.length, this.lastFile.get(mood) ?? -1, Math.random);
      this.lastFile.set(mood, i);
      const f = files[i];
      this.track = new FileTrack(this.am.ctx, dest, AUDIO_BASE + f, () => this.badFiles.add(f));
    } else {
      this.track = new ProceduralPiece(this.am.ctx, dest, this.reverb, mood);
    }
  }
}
